import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Grid,
  Typography,
  List,
  ListItem,
} from "@mui/material";
import {
  StyledBlackInformationTypography,
  StyledDarkRedInformationTypography,
  StyledMainBox,
} from "../../../components/commonComponents/StyledComponents/styled";
import { Close } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import { checkUserRole } from "../../../api_calls/userManagement";
import { loadRetailerAccessAvailability } from "../../../api_calls/retailerSelection";
import {
  resolveLoggedInUserType,
  shouldUseRetailerStoreHierarchy,
} from "../../../components/commonComponents/Filters/storeFilterUtils";

const compareLabels = (left, right) =>
  String(left).localeCompare(String(right), undefined, { numeric: true });

// Same group order as the store dropdown: numeric prefixes first, then names.
const compareGroupNames = (left, right) => {
  const leftNumber = parseInt(String(left).split("-")[0], 10);
  const rightNumber = parseInt(String(right).split("-")[0], 10);
  const leftHasNumber = !Number.isNaN(leftNumber);
  const rightHasNumber = !Number.isNaN(rightNumber);

  if (leftHasNumber && rightHasNumber) return leftNumber - rightNumber;
  return compareLabels(left, right);
};

const groupStoresByRetailer = (storeOptions) => {
  const groups = {};

  storeOptions.forEach((storeOption) => {
    const groupName = storeOption.root_retailer_company_name;
    if (!groups[groupName]) groups[groupName] = [];
    groups[groupName].push(storeOption);
  });

  return Object.keys(groups)
    .sort(compareGroupNames)
    .map((groupName) => ({
      groupName,
      stores: groups[groupName].slice().sort((left, right) => compareLabels(left.label, right.label)),
    }));
};

const StoreInfo = ({ open, usedFor, size, closeModal, storeInfo, dialogTitle }) => {
  // Set a threshold for when to display the two-column layout
  const threshold = 5;
  const currentStateData = useSelector((state) => state.userSelections);
  const [retailerHierarchyAvailable, setRetailerHierarchyAvailable] = React.useState(false);
  const loggedInUserType = resolveLoggedInUserType();
  const isBrandUser =
    currentStateData?.brand_report_view === true || checkUserRole()?.user_role === "Brand";
  const storeOptions = Array.isArray(storeInfo?.selected_store_options)
    ? storeInfo.selected_store_options
    : [];
  const showRetailerHierarchy = shouldUseRetailerStoreHierarchy({
    isBrand: isBrandUser,
    retailerHierarchyAvailable,
    storeOptions,
  });

  React.useEffect(() => {
    let shouldApplyResult = true;

    if (loggedInUserType === "") {
      setRetailerHierarchyAvailable(false);
      return undefined;
    }

    loadRetailerAccessAvailability(loggedInUserType)
      .then((retailerAccessInfo) => {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(retailerAccessInfo.available === true);
        }
      })
      .catch(() => {
        if (shouldApplyResult) {
          setRetailerHierarchyAvailable(false);
        }
      });

    return () => {
      shouldApplyResult = false;
    };
  }, [loggedInUserType]);

  const storeLabels = storeOptions
    .slice()
    .sort((next, prev) => compareLabels(next.label, prev.label))
    .map((store) => store.label);
  const groupedStores = showRetailerHierarchy === true ? groupStoresByRetailer(storeOptions) : [];

  const renderStoreBullet = (label, key) => (
    <ListItem key={key} disablePadding>
      <Typography variant="body2">• {label}</Typography>
    </ListItem>
  );

  // Two columns once the list is longer than the threshold. One column otherwise.
  const renderStoreColumns = (labels, keyPrefix) => {
    if (labels.length > threshold) {
      const middleIndex = Math.ceil(labels.length / 2);
      const firstColumn = labels.slice(0, middleIndex);
      const secondColumn = labels.slice(middleIndex);

      return (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <List>
              {firstColumn.map((label, index) => renderStoreBullet(label, `${keyPrefix}-a-${index}`))}
            </List>
          </Grid>
          <Grid item xs={6}>
            <List>
              {secondColumn.map((label, index) => renderStoreBullet(label, `${keyPrefix}-b-${index}`))}
            </List>
          </Grid>
        </Grid>
      );
    }

    return (
      <List disablePadding>
        {labels.map((label, index) => renderStoreBullet(label, `${keyPrefix}-${index}`))}
      </List>
    );
  };

  // Function to render stores in one or two columns based on the threshold
  const renderFlatStoreList = () => {
    if (storeLabels.length === 0) {
      return <Typography>Stores</Typography>;
    }

    return renderStoreColumns(storeLabels, "store");
  };

  const renderGroupedStoreList = () => {
    const singleRetailer = groupedStores.length === 1;

    return (
      <Box>
        {groupedStores.map((group) => {
          const labels = group.stores.map((store) => store.label);
          // One retailer with a long list uses the same two-column split as the flat list.
          const splitColumns = singleRetailer === true && labels.length > threshold;

          return (
            <Box key={group.groupName} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {group.groupName}
              </Typography>
              {splitColumns === true ? (
                renderStoreColumns(labels, group.groupName)
              ) : (
                <List disablePadding sx={{ pl: 1 }}>
                  {group.stores.map((store, index) =>
                    renderStoreBullet(store.label, `${group.groupName}-${store.id ?? index}`)
                  )}
                </List>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(e) => {
        e.preventDefault();
      }}
      fullWidth={true}
      maxWidth={size}
    >
      <DialogTitle> {dialogTitle}
        {/* {usedFor === "clips" ? "Clip Stores" : "Picture Stores"} */}
      </DialogTitle>
      <DialogContent>
        <Box>
          <Stack direction={"row"} spacing={1}>
            <StyledDarkRedInformationTypography>
              {/* {usedFor === "clips" ? "Clip" : "Picture"}  */}
              {usedFor} :
            </StyledDarkRedInformationTypography>
            <StyledBlackInformationTypography>{storeInfo.name}</StyledBlackInformationTypography>
          </Stack>
        </Box>
        <Box height={20} />
        <Box>
          <Stack direction={"row"} spacing={1}>
            <StyledDarkRedInformationTypography>Total number of stores :</StyledDarkRedInformationTypography>
            <StyledBlackInformationTypography>{storeInfo.stores}</StyledBlackInformationTypography>
          </Stack>
        </Box>
        <Box height={20} />
        <Box>
          <Stack direction={"row"} spacing={1}>
            <StyledBlackInformationTypography>List of your stores:</StyledBlackInformationTypography>
          </Stack>
        </Box>

        <StyledMainBox component="main">
          <Box sx={{ display: "flex" }}>
            {showRetailerHierarchy === true ? renderGroupedStoreList() : renderFlatStoreList()}
          </Box>
        </StyledMainBox>
      </DialogContent>
      <Box height={20} />
      <DialogActions>
        <Stack direction={"row"} spacing={2}>
          <CloseButton
            onClick={(e) => {
              e.preventDefault();
              closeModal();
            }}
            label="Close"
            icon={<Close />}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default StoreInfo;
